// WallpaperPicker — selector de fondo en la TopBar (oculto en mobile).
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Palette } from "lucide-react";

import { WALLPAPERS, useWallpaper, type Wallpaper } from "@/lib/wallpapers";

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
        {label}
      </div>
      <div className="grid grid-cols-2 gap-2">{children}</div>
    </div>
  );
}

function Tile({ w, selected, onSelect }: { w: Wallpaper; selected: boolean; onSelect: () => void }) {
  return (
    <button
      role="menuitemradio"
      aria-checked={selected}
      onClick={onSelect}
      title={w.label}
      className={`relative aspect-[16/10] overflow-hidden rounded-xl border transition ${
        selected
          ? "border-[var(--role-accent)] ring-2 ring-[rgb(var(--role-accent-rgb)/0.4)]"
          : "border-white/10 hover:border-white/25"
      }`}
      style={{ background: "var(--color-base)" }}
    >
      {w.type === "image" ? (
        <img src={w.url} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <>
          <span className="absolute inset-0" style={{ background: w.base }} />
          <span
            className="absolute -left-2 -top-2 h-12 w-12 rounded-full"
            style={{ background: `radial-gradient(circle, ${w.orbs[0]} 0%, transparent 70%)`, filter: "blur(8px)" }}
          />
        </>
      )}
      <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 to-transparent px-2 py-1 text-[11px] font-semibold text-white">
        {w.label}
      </span>
      {selected && (
        <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--role-accent)] text-white">
          <Check size={11} />
        </span>
      )}
    </button>
  );
}

export function WallpaperPicker({ roleDefault }: { roleDefault: string }) {
  const [active, setActive] = useWallpaper(roleDefault);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = WALLPAPERS[active] ?? WALLPAPERS.aurora!;
  const all = Object.values(WALLPAPERS);
  const colors = all.filter((w) => w.type !== "image");
  const images = all.filter((w) => w.type === "image");

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative hidden sm:block">
      <button
        onClick={() => setOpen((o) => !o)}
        className="vision-focusable flex h-9 items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-2.5 transition hover:bg-white/10"
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Fondo: ${current.label}`}
      >
        <Palette size={15} className="text-[var(--color-text-secondary)]" />
        <span className="flex -space-x-1.5">
          {current.swatch.map((c, i) => (
            <span
              key={i}
              className="h-3 w-3 rounded-full ring-2 ring-[var(--color-base)]"
              style={{ background: c }}
            />
          ))}
        </span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -6 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
            role="menu"
            className="vision-modal-glass absolute right-0 top-12 w-[300px] p-3"
          >
            <div className="max-h-[70vh] overflow-y-auto pr-1">
              <Section label="Colores">
                {colors.map((w) => (
                  <Tile key={w.key} w={w} selected={w.key === active} onSelect={() => setActive(w.key)} />
                ))}
              </Section>
              <Section label="Imágenes">
                {images.map((w) => (
                  <Tile key={w.key} w={w} selected={w.key === active} onSelect={() => setActive(w.key)} />
                ))}
              </Section>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
